import React from "react";
import { Box, Typography } from "@mui/material";
import moment from "moment";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import { stageName, stageStatusMeta } from "../utils/relianceStages";
import { shortLabel } from "../utils/policyParts";

const NAVY = "#1E3A5F";
const FONT = "Inter, Poppins, sans-serif";
const TONE = {
  completed: { color: "#067647", bg: "#ECFDF3", border: "#ABEFC6" },
  failed: { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA" },
  pending: { color: "#475467", bg: "#F2F4F7", border: "#EAECF0" },
};

const KIND = {
  status: "Status changed by hand",
  kycFiles: "KYC files changed",
  reset: "Set back to Pending",
  tpPolicy: "TP policy details entered",
};

const TP_LINES = [
  ["existingTpPolicyNumber", "TP Policy No"],
  ["existingTpStartDate", "TP Start"],
  ["existingTpEndDate", "TP End"],
];
const tpValue = (field, v) => (!v ? "none" : /Date$/.test(field) ? moment(v).format("DD MMM YYYY") : String(v));
const RUN = {
  now: "Run started now",
  later: "Runs when the automation is free",
  none: "Nothing left to run",
};

const StatusPill = ({ status }) => {
  const meta = stageStatusMeta(status);
  const tone = TONE[meta.status] || TONE.pending;
  return (
    <Box component="span" sx={{ px: "7px", py: "1px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, fontFamily: FONT, color: tone.color, backgroundColor: tone.bg, border: `1px solid ${tone.border}`, whiteSpace: "nowrap" }}>
      {meta.label}
    </Box>
  );
};

const Line = ({ label, children }) => (
  <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap", mt: "4px" }}>
    <Typography component="span" sx={{ fontSize: "11.5px", color: "#667085", fontFamily: FONT, minWidth: 64 }}>
      {label}
    </Typography>
    <Box component="span" sx={{ fontSize: "12px", color: "#101828", fontFamily: FONT, minWidth: 0, wordBreak: "break-word" }}>
      {children}
    </Box>
  </Box>
);

/**
 * The History dialog's "Manual changes" (onlinePolicy.stageLog, 2026-10-19): every step changed by a person — Edit ->
 * Status, Edit -> KYC files, "Set back to Pending" — with who and when, the part, each step old -> new, the referral
 * number / page, the files, the reason and what ran. Newest first.
 */
const StageLogList = ({ entries }) => {
  const list = [...(Array.isArray(entries) ? entries : [])].reverse();
  if (!list.length) {
    return <Typography sx={{ fontSize: "12.5px", color: "#667085", fontFamily: FONT, py: 1 }}>No step was changed by hand.</Typography>;
  }
  return (
    <Box>
      {list.map((e, i) => (
        <Box key={i} data-testid={`stage-log-${i}`} sx={{ py: 1.25, borderBottom: i < list.length - 1 ? "1px dashed #EAECF0" : "none" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: NAVY, fontFamily: FONT }}>
              {KIND[e.kind] || "Steps changed"}
              {e.insurer || e.part ? (
                <Box component="span" sx={{ fontWeight: 500, color: "#475467" }}>
                  {" · "}
                  {[e.insurer, e.part && !String(e.part).includes(",") ? shortLabel(e.part) : null].filter(Boolean).join(" ")}
                </Box>
              ) : null}
            </Typography>
            <Typography sx={{ fontSize: "11.5px", color: "#667085", fontFamily: FONT }}>
              {e.byName || e.by ? `by ${e.byName || e.by} · ` : ""}
              {e.at ? moment(e.at).format("DD MMM YYYY, hh:mm A") : ""}
            </Typography>
          </Box>

          {(e.changes || []).length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", mt: 0.75 }}>
              {e.changes.map((c, j) => (
                <Box key={j} sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                  <Typography component="span" sx={{ fontSize: "12px", fontWeight: 600, color: "#344054", fontFamily: FONT, minWidth: 64 }}>
                    {stageName(c.stage)}
                    {c.part ? ` (${shortLabel(c.part)})` : ""}
                  </Typography>
                  <StatusPill status={c.from} />
                  <EastRoundedIcon sx={{ fontSize: 14, color: "#98A2B3" }} />
                  <StatusPill status={c.to} />
                </Box>
              ))}
            </Box>
          ) : null}

          {e.proposalNumber ? (
            <Line label="Referral No">
              <b>{e.proposalNumber}</b>
              {e.previousProposalNumber ? ` (was ${e.previousProposalNumber})` : ""}
            </Line>
          ) : e.previousProposalNumber ? (
            <Line label="Old ref. No">{e.previousProposalNumber}</Line>
          ) : null}
          {e.link ? <Line label="Page">{e.link}</Line> : null}
          {(e.files || []).map((f, j) => (
            <Line key={`f${j}`} label={f.label || f.field}>
              <Box component="span" sx={{ color: "#B42318", textDecoration: f.from ? "line-through" : "none" }}>
                {f.from || "none"}
              </Box>
              {" → "}
              <Box component="span" sx={{ color: "#067647", fontWeight: 600 }}>
                {f.uploadedName || f.to}
              </Box>
            </Line>
          ))}
          {/* the TP policy's number / dates entered with it (Edit -> Status on the TP part, Edit -> TP policy details) */}
          {e.tpPolicy && e.tpPolicy.to
            ? TP_LINES.filter(([f]) => f in e.tpPolicy.to).map(([f, label]) => (
                <Line key={f} label={label}>
                  {e.tpPolicy.from && e.tpPolicy.from[f] ? `${tpValue(f, e.tpPolicy.from[f])} → ` : ""}
                  <b>{tpValue(f, e.tpPolicy.to[f])}</b>
                </Line>
              ))
            : null}
          {e.reason ? <Line label="Reason">“{e.reason}”</Line> : null}
          {e.run ? <Line label="Then">{RUN[e.run] || e.run}</Line> : null}
        </Box>
      ))}
    </Box>
  );
};

export default StageLogList;
